import { ExperimentsSelectors, FirebaseSelectors, ProgressSelectors, RemoteConfigService } from '@web_opxp/features';
import { DEFAULT_CHALLENGE } from '../base/constants';
import { getDayOfWeekFromNow, isDateToday, isDayInPastSevenDays } from '../base/dateUtils';
import ENV from '../base/env';
import { IState } from '../store/state';
import { ChallengeExperimentId } from './constants';
import { TChallengeWorkoutStatus } from './types';

export const isChallengeActive = (state: IState) => {
  const experimentActive = ExperimentsSelectors.isExperimentEnabled(state, ChallengeExperimentId);
  const firebaseReady = FirebaseSelectors.isReady(state);
  if (!experimentActive || !firebaseReady) {
    return false;
  }
  const challengeConfig = RemoteConfigService.getObjectConfigValue(ENV.RemoteConfigChallenge, DEFAULT_CHALLENGE);
  const startsAt = challengeConfig?.startsAt;
  const workoutIds = challengeConfig?.challenge_workout_ids;
  if (!startsAt || !workoutIds || !workoutIds.length) {
    return false;
  }
  const dayOfChallengeWeek = getDayOfWeekFromNow(startsAt);
  const isOver = dayOfChallengeWeek >= workoutIds.length;
  const isStarted = dayOfChallengeWeek >= 0;
  return isStarted && !isOver;
};

export const isFirstDayOfChallenge = () => {
  const config = RemoteConfigService.getObjectConfigValue(ENV.RemoteConfigChallenge, DEFAULT_CHALLENGE);
  const startsAt = config?.startsAt;
  if (!startsAt) {
    return false;
  }

  const dayOfChallengeWeek = getDayOfWeekFromNow(startsAt);
  return dayOfChallengeWeek === 0;
};

export const isLastDayOfChallenge = () => {
  const config = RemoteConfigService.getObjectConfigValue(ENV.RemoteConfigChallenge, DEFAULT_CHALLENGE);
  const startsAt = config?.startsAt;
  if (!startsAt) {
    return false;
  }
  const workoutIds = config?.challenge_workout_ids;
  if (!workoutIds || !workoutIds.length) {
    return false;
  }
  const dayOfChallengeWeek = getDayOfWeekFromNow(startsAt);
  return dayOfChallengeWeek === workoutIds.length - 1;
};

export const getChallengeWorkoutIds = () => {
  const config = RemoteConfigService.getObjectConfigValue(ENV.RemoteConfigChallenge, DEFAULT_CHALLENGE);

  return config?.challenge_workout_ids || [];
};

export const getTodaysChallengeWorkoutId = () => {
  const config = RemoteConfigService.getObjectConfigValue(ENV.RemoteConfigChallenge, DEFAULT_CHALLENGE);
  const workoutIds = config?.challenge_workout_ids;
  const startsAt = config?.startsAt;
  if (!workoutIds || !startsAt) {
    return null;
  }

  const dayOfChallengeWeek = getDayOfWeekFromNow(startsAt);
  if (dayOfChallengeWeek < 0 || dayOfChallengeWeek >= workoutIds.length) {
    // challenge is over or not started - no workouts
    return null;
  }
  return workoutIds[dayOfChallengeWeek];
};

export const getChallengeLength = () => {
  const config = RemoteConfigService.getObjectConfigValue(ENV.RemoteConfigChallenge, DEFAULT_CHALLENGE);
  const workoutIds = config?.challenge_workout_ids ?? [];
  return workoutIds.length;
};

export const getChallengeProgressStatus: (state: IState) => Array<TChallengeWorkoutStatus> = (state) => {
  const firebaseReady = FirebaseSelectors.isReady(state);
  if (!firebaseReady) {
    return [];
  }
  const config = RemoteConfigService.getObjectConfigValue(ENV.RemoteConfigChallenge, DEFAULT_CHALLENGE);
  const startsAt = config?.startsAt;
  if (!startsAt) {
    return [];
  }
  // day of week is zero-based, i.e. today is day = 0
  const dayOfChallengeWeek = getDayOfWeekFromNow(startsAt);
  const workoutIds = config?.challenge_workout_ids ?? [];
  const result = getChallengeWorkoutStatuses(state, workoutIds, dayOfChallengeWeek);
  // check if challenge completed fully (i.e. all 7 days)
  if (
    !result.includes('skipped') &&
    !result.includes('not-joined') &&
    result[result.length - 1] === 'current-completed'
  ) {
    result[result.length - 1] = 'final-completed';
  }
  return result;
};

// selectors migrated from progress feature
// this is a more appropriate place for such selectors
export const hasCompletedTodaysChallenge = (state: IState, workoutId: number | null) => {
  if (!workoutId) {
    return false;
  }

  const timestamp = ProgressSelectors.getLastWorkoutTimestamp(state, workoutId);

  if (!timestamp) {
    return false;
  }
  return isDateToday(timestamp);
};

export const hasCompletedAChallengeWorkout = (state: IState, workoutIds: number[]) => {
  if (!workoutIds) {
    return;
  }

  // If there are any workouts with a challenge workout id in progress, user has completed a challenge
  for (const workoutId of workoutIds) {
    const timestamp = ProgressSelectors.getLastWorkoutTimestamp(state, workoutId);
    if (timestamp) {
      const completedInThisChallenge = isDayInPastSevenDays(timestamp);
      if (completedInThisChallenge) {
        return true;
      }
    }
  }
  return false;
};

function getChallengeWorkoutStatuses(state: IState, workoutIds: number[], dayOfChallengeWeek: number) {
  let joined = false;
  const currentOrCompleted = (isLastWorkout: boolean, isCurrentDay: boolean): TChallengeWorkoutStatus => {
    if (isLastWorkout) {
      return 'current-completed';
    }
    joined = true;
    if (isCurrentDay) {
      return 'current-completed';
    }
    return 'completed';
  };

  const result: TChallengeWorkoutStatus[] = workoutIds.map((wId, index) => {
    const timestamp = ProgressSelectors.getLastWorkoutTimestamp(state, wId);
    const completedInThisChallenge = isDayInPastSevenDays(timestamp);

    const isLastWorkout = index === workoutIds.length - 1;
    const isCurrentDay = dayOfChallengeWeek === index;

    if (completedInThisChallenge) {
      return currentOrCompleted(isLastWorkout, isCurrentDay);
    }

    if (isLastWorkout && !isCurrentDay) {
      return 'final-future';
    }

    if (isCurrentDay) {
      return 'current';
    }

    if (dayOfChallengeWeek < index) {
      return 'future';
    }
    if (joined) {
      return 'skipped';
    }
    return 'not-joined';
  });
  return result;
}
